/*
  Значки разделов - 09.09.2026, решение владельца «облагородить левое меню».

  Почему рисуем сами, а не берём готовый набор. Библиотека значков - это ещё
  одна зависимость ради десяти картинок, и она тянет за собой сотни неиспользуемых
  (в сборку они не попадут, но в `node_modules` и в аудит уязвимостей - да).
  Десять контуров занимают меньше, чем строка `import` из такой библиотеки.

  Три правила, которые нельзя нарушить при добавлении нового значка:

  1. Цвет - `currentColor`, никогда литералом. Значок в меню белый на тёмном, а в
     полосе телефона - тот же значок на том же фоне; захардкоженный цвет пришлось
     бы красить в двух местах, и при смене палитры один из них отстал бы
     (правило 6 скилла `frontend`).
  2. Контур, а не заливка: заливка при кегле 18 пикселей превращается в пятно, по
     которому раздел не узнать. Толщина 1.6 - тоньше теряется на дешёвом мониторе,
     толще спорит с текстом пункта.
  3. `aria-hidden` - обязательно. Название раздела стоит рядом словами, и читалка
     экрана, объявив ещё и картинку, прочитает пункт дважды.

  Сетка у всех одна - 24 на 24, иначе значки одного меню стоят на разной высоте.
*/

// Контуры. Ключ - имя раздела, значение - разметка внутри <svg>.
// Разложены по смыслу, а не по алфавиту: порядок тот же, что в меню.
const SHAPES = {
  // Дашборд - четыре плитки: то, из чего экран и состоит.
  dashboard: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),

  // Реестр - строки списка с отметками слева.
  registry: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4.5 6h.01M4.5 12h.01M4.5 18h.01" />
    </>
  ),

  // Конвейер - движение слева направо: счета идут очередью к подаче.
  conveyor: (
    <>
      <path d="M3 7h11l-2.5-2.5M3 7l3 3" />
      <path d="M21 17H10l2.5 2.5M21 17l-3-3" />
    </>
  ),

  // Загрузка - стрелка вверх над лотком: файл выгрузки кладут в систему.
  import: (
    <>
      <path d="M12 15V4m0 0L8 8m4-4 4 4" />
      <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
    </>
  ),

  // Расход - кошелёк: сколько потрачено на действия по портфелю. Клапан и
  // застёжка обязательны: без них при кегле 20 пикселей остаётся прямоугольник,
  // в котором кошелька не узнать (проверено осмотром 09.09.2026).
  usage: (
    <>
      <rect x="3" y="6" width="18" height="13" rx="2.5" />
      <path d="M3 10.5h18" />
      <circle cx="16.5" cy="14.75" r="1.15" />
    </>
  ),

  // Радар - круги развёртки и отметка: поводы вернуться к старым делам.
  radar: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 12 18 6.4" />
      <circle cx="12" cy="12" r="1" />
    </>
  ),

  // Коммуникации - реплика разговора с должником.
  communications: (
    <>
      <path d="M20.5 13.5a2 2 0 0 1-2 2H9l-4.5 3.5v-3.5h-.5a1.5 1.5 0 0 1-1.5-1.5v-8A2 2 0 0 1 4.5 4h14a2 2 0 0 1 2 2z" />
      <path d="M7.5 8h9M7.5 11.5h5.5" />
    </>
  ),

  // Исполнение - здание с колоннами: суд, банк, служба приставов.
  enforcement: (
    <>
      <path d="M3.5 9.5 12 4.5l8.5 5" />
      <path d="M5.5 9.5v8M10 9.5v8M14 9.5v8M18.5 9.5v8" />
      <path d="M3 19.5h18" />
    </>
  ),

  // Отчёты - столбцы: что сделано за период и чем это кончилось.
  reports: (
    <>
      <path d="M3.5 20.5h17" />
      <path d="M7 20.5v-6M12 20.5V6.5M17 20.5v-9" />
    </>
  ),

  // Настройки - шестерёнка, общепринятый знак; изобретать здесь свой нельзя.
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.2 14.4a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a1.9 1.9 0 1 1-3.8 0v-.2a1.6 1.6 0 0 0-2.8-1.1l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.3a1.9 1.9 0 1 1 0-3.8h.2a1.6 1.6 0 0 0 1.1-2.8l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 1-1.5v-.3a1.9 1.9 0 1 1 3.8 0v.2a1.6 1.6 0 0 0 2.7 1.1l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.3a1.9 1.9 0 1 1 0 3.8h-.2a1.6 1.6 0 0 0-1.5 1z" />
    </>
  ),
}

/*
  Значок раздела. Имени нет в наборе - не рисуем ничего: пустое место в меню
  честнее чужой картинки, по которой человек решит, что попал не туда.
*/
export function SectionIcon({ name }) {
  const shape = SHAPES[name]
  if (!shape) return null

  return (
    <svg
      className="side__icon"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {shape}
    </svg>
  )
}

/*
  Знак продукта - круг с буквой «А», общий для всей экосистемы: тот же контур
  стоит в Арбитр ДА и Арбитр АИ, отличается только цвет круга (там
  сине-фиолетовый, здесь петролевый). Решение владельца 09.09.2026: три продукта
  должны читаться одной семьёй, а различаться цветом, а не рисунком.

  Показывается КАРТИНКОЙ из того же файла, что и значок вкладки, а не
  повторяется здесь разметкой. Иначе знак живёт в двух местах: правка контура в
  одном оставит другое прежним, и вкладка разойдётся с углом кабинета - именно
  то расхождение, ради которого знак и заводили.
*/
export function BrandMark() {
  return (
    <img
      className="brandmark"
      // `?v=2` - та же метка, что в index.html: браузер успел запомнить прежний
      // битый файл значка, и без смены адреса продолжал бы показывать пустоту.
      src="/favicon.svg?v=2"
      width="28"
      height="28"
      alt=""
      aria-hidden="true"
    />
  )
}
